import * as yup from "yup";

import i18n from "lib/i18n";
import XRegExp from "xregexp"; // extended regex syntax, so we can match UTF-8 chars

const emailRegex = XRegExp("^[\\p{Letter}\\w+\\-.]+@[a-z\\d-]+(\\.[a-z\\d-]+)*\\.[a-z]+$", "i");

export default class Validation {
  constructor(context) {
    this.schema = Validation.buildSchema();
    this.context = context;
  }

  /**
   * Build the yup validation schema
   * https://github.com/jquense/yup
   *
   * This schema accepts several context variables (eg. $minAmount), the
   * values of which are supplied in the construction of this class.
   */
  static buildSchema() {
    return yup.object().shape({
      amount: yup.number()
        .required(i18n.t("donation.message.amount_range_error"))
        .typeError(i18n.t("donation.message.amount_range_error"))
        .min(yup.ref("$minAmount"), i18n.t("donation.message.amount_range_error"))
        .max(yup.ref("$maxAmount"), i18n.t("donation.message.amount_range_error")),
      cardNumber: yup.boolean().when("paymentProvider", {
        is: "credit_card",
        then: yup.boolean()
          .required(i18n.t("donation.message.credit_card_error"))
          .equals([true], i18n.t("donation.message.credit_card_error")),
        otherwise: undefined,
      }),
      cvv: yup.boolean().when("paymentProvider", {
        is: "credit_card",
        then: yup.boolean()
          .required(" ")
          .equals([true], " "),
        otherwise: undefined,
      }),
      // We use a custom regex here instead of yup's built-in email validation because
      // we want our validation to exactly match the one we use server-side.
      // If the client-side validation is looser than the server-side validation,
      // we'll see some email addresses being accepted as valid on the client,
      // payment taken, and then they'll be knocked back when we go to save the
      // donation on the server.
      email: yup.string()
        .required(i18n.t("donation.message.email_error"))
        .matches(emailRegex, i18n.t("donation.message.email_error")),
      name: yup.string()
        .required(i18n.t("donation.message.name_error"))
        .matches(/^[^@]+$/, i18n.t("donation.message.name_error")),
      address: yup.string().when("$validateAddresses", {
        is: true,
        then: yup.string().required(i18n.t("donation.message.address_error")),
        otherwise: undefined,
      }),
      postcode: yup.string().when("$validateAddresses", {
        is: true,
        then: yup.string().required(i18n.t("donation.message.postcode_error")),
        otherwise: undefined,
      }),
    });
  }

  /**
   * Validate the state of the donation form
   *
   * Returns a promise that resolves if validation is successful, and is
   * rejected with errors otherwise. Errors are formatted as:
   *   [{field: "message"}]
   */
  validate(state) {
    return new Promise((resolve, reject) => {
      this.schema.validate(state, { abortEarly: false, context: this.context })
        .then(() => resolve())
        .catch(err => reject(Validation.reformatErrors(err)));
    });
  }

  static reformatErrors(err) {
    return Object.fromEntries(err.inner.map(ex => [ex.path, ex.message]));
  }
}
